/**
 * ShareListModal Component
 *
 * Modal for creating a shareable link to the current filtered results.
 * Supports optional password protection and expiration.
 */

import { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Switch,
  DatePicker,
  Button,
  message,
  Typography,
  Space,
  Tooltip,
} from 'antd';
import {
  ShareAltOutlined,
  LockOutlined,
  CopyOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { createSharedList, getSharedListUrl } from '@/services/sharedListApi';
import type { PlaceFilter } from '@/types/place';
import type { Dayjs } from 'dayjs';

const { Text, Paragraph } = Typography;

interface ShareListModalProps {
  open: boolean;
  onClose: () => void;
  filters: PlaceFilter;
  visibleColumns: string[];
  selectedPlaceIds?: string[];
  totalCount?: number;
}

export function ShareListModal({
  open,
  onClose,
  filters,
  visibleColumns,
  selectedPlaceIds,
  totalCount,
}: ShareListModalProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [createdSlug, setCreatedSlug] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const sharedList = await createSharedList({
        name: values.name || undefined,
        description: values.description || undefined,
        password: values.usePassword ? values.password : undefined,
        filterConfig: filters,
        placeIds: selectedPlaceIds && selectedPlaceIds.length > 0 ? selectedPlaceIds : undefined,
        visibleColumns,
        expiresAt: values.expiresAt ? values.expiresAt.toISOString() : undefined,
      });

      setCreatedSlug(sharedList.slug);
      message.success('Shared list created!');
    } catch (error) {
      console.error('Failed to create shared list:', error);
      message.error('Failed to create shared list');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!createdSlug) return;

    const url = getSharedListUrl(createdSlug);
    await navigator.clipboard.writeText(url);
    setCopied(true);
    message.success('Link copied to clipboard!');

    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    form.resetFields();
    setCreatedSlug(null);
    setCopied(false);
    onClose();
  };

  const shareUrl = createdSlug ? getSharedListUrl(createdSlug) : null;

  return (
    <Modal
      title={
        <Space>
          <ShareAltOutlined />
          Share Results
        </Space>
      }
      open={open}
      onCancel={handleClose}
      footer={
        createdSlug ? (
          <Button type="primary" onClick={handleClose}>
            Done
          </Button>
        ) : (
          <Space>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="primary" onClick={handleSubmit} loading={loading}>
              Create Share Link
            </Button>
          </Space>
        )
      }
      width={500}
    >
      {createdSlug ? (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <CheckOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
          <Paragraph strong style={{ fontSize: 16 }}>
            Your share link is ready!
          </Paragraph>
          <div
            style={{
              background: '#f5f5f5',
              padding: '12px 16px',
              borderRadius: 8,
              marginBottom: 16,
              wordBreak: 'break-all',
            }}
          >
            <Text code>{shareUrl}</Text>
          </div>
          <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
            <Button
              type="primary"
              icon={copied ? <CheckOutlined /> : <CopyOutlined />}
              onClick={handleCopy}
            >
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
          </Tooltip>
        </div>
      ) : (
        <Form form={form} layout="vertical" initialValues={{ usePassword: false }}>
          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Create a shareable link to these results.
            {totalCount !== undefined && (
              <strong> {totalCount.toLocaleString()} places</strong>
            )}
            {selectedPlaceIds && selectedPlaceIds.length > 0 && (
              <strong> ({selectedPlaceIds.length} selected)</strong>
            )}
          </Paragraph>

          <Form.Item name="name" label="Name (optional)">
            <Input placeholder="e.g., Restaurants in Munich" maxLength={255} />
          </Form.Item>

          <Form.Item name="description" label="Description (optional)">
            <Input.TextArea
              placeholder="Add a note about this shared list..."
              maxLength={1000}
              rows={2}
            />
          </Form.Item>

          <Form.Item
            name="usePassword"
            label="Password Protection"
            valuePropName="checked"
          >
            <Switch checkedChildren={<LockOutlined />} />
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.usePassword !== curr.usePassword}>
            {({ getFieldValue }) =>
              getFieldValue('usePassword') && (
                <Form.Item
                  name="password"
                  label="Password"
                  rules={[
                    { required: true, message: 'Please enter a password' },
                    { min: 4, message: 'Password must be at least 4 characters' },
                  ]}
                >
                  <Input.Password placeholder="Enter password" maxLength={100} />
                </Form.Item>
              )
            }
          </Form.Item>

          <Form.Item name="expiresAt" label="Expiration (optional)">
            <DatePicker
              showTime
              placeholder="Link never expires"
              style={{ width: '100%' }}
              disabledDate={(current) => current && current.isBefore(new Date(), 'day')}
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}

export default ShareListModal;
